import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Select, MenuItem } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { useMetrics } from "../../../shared/context/metricsContext"
import { BarChart } from '@mui/x-charts/BarChart';
import { useEffect, useState } from "react";

const MONTHS = [
    { full: 'enero', short: 'Ene', value: 1 },
    { full: 'febrero', short: 'Feb', value: 2 },
    { full: 'marzo', short: 'Mar', value: 3 },
    { full: 'abril', short: 'Abr', value: 4 },
    { full: 'mayo', short: 'May', value: 5 },
    { full: 'junio', short: 'Jun', value: 6 },
    { full: 'julio', short: 'Jul', value: 7 },
    { full: 'agosto', short: 'Ago', value: 8 },
    { full: 'septiembre', short: 'Sep', value: 9 },
    { full: 'octubre', short: 'Oct', value: 10 },
    { full: 'noviembre', short: 'Nov', value: 11 },
    { full: 'diciembre', short: 'Dic', value: 12 }
];

export default function TaskScheduledPerMonthMetrics() {
    const theme = useTheme();
    const { tasksScheduledPerMonth, loadingTasksScheduledPerMonth, getTasksScheduledPerMonth, currentLocation } = useMetrics();

    const [selectMonth, setSelectMonth] = useState(new Date().getMonth() + 1);

    useEffect(() => {
        if (currentLocation?.name) {
            getTasksScheduledPerMonth({ month: selectMonth });
        }
    }, [currentLocation]);


    useEffect(() => {
        if (selectMonth) {
            getTasksScheduledPerMonth({ month: selectMonth });
        }
    }, [selectMonth]);

    useEffect(() => {
        if (tasksScheduledPerMonth?.length > 0) {
            console.log('Datos recibidos:', tasksScheduledPerMonth);
            console.log('Buscando sede:', currentLocation?.name);
            const found = tasksScheduledPerMonth.find(data => data.sede.trim() === currentLocation?.name?.trim());
            console.log('Datos encontrados:', found);
        }
    }, [tasksScheduledPerMonth, currentLocation]);

    if (!currentLocation?.name) {
        return (
            <Box sx={{
                width: '100%',
                backgroundColor: theme.palette.background.paper,
                padding: theme.spacing(2),
                boxShadow: theme.palette.shadow.default,
                borderRadius: theme.spacing(1),
                textAlign: 'center'
            }}>
                <Typography>Por favor seleccione una sede</Typography>
            </Box>
        );
    }

    if (loadingTasksScheduledPerMonth) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    const currentYear = new Date().getFullYear();
    const currentLocationData = tasksScheduledPerMonth?.find(data => 
        data.sede.trim().toLowerCase() === currentLocation.name.trim().toLowerCase()
    );
    
    // Crear un array con todos los meses del año, con datos o sin datos
    const completeMetrics = MONTHS.map(month => {
        const existingMetric = currentLocationData?.metrics?.find(
            metric => metric.month.toLowerCase() === month.full.toLowerCase()
        );
        
        return {
            month: month.full,
            monthShort: month.short,
            total_tasks_executed: existingMetric?.total_tasks_executed || 0,
            total_tasks_scheduled: existingMetric?.total_tasks_scheduled || 0
        };
    });

    const chartData = completeMetrics.map(metric => ({
        mes: metric.month,
        porcentaje: metric.total_tasks_scheduled > 0 
            ? (metric.total_tasks_executed / metric.total_tasks_scheduled) * 100 
            : 0
    }));

    return (
        <Box sx={{
            width: '100%',
            backgroundColor: theme.palette.background.paper,
            padding: theme.spacing(2),
            boxShadow: theme.palette.shadow.default,
            borderRadius: theme.spacing(1),
            display: 'flex',
            flexDirection: 'column',
            gap: 3
        }}>
            <Typography variant="h6" sx={{
                fontWeight: 'bold',
                fontSize: { xs: '1rem', sm: '1.2rem' },
                color: theme.palette.text.primary,
                textAlign: 'center',
                lineHeight: 1
            }}>
                Tareas programadas y ejecutadas por mes {currentYear} 
            </Typography>

            <Typography variant="subtitle1" sx={{
                fontWeight: 'bold',
                fontSize: { xs: '0.9rem', sm: '1rem' },
                color: theme.palette.text.primary,
                textAlign: 'center',
                lineHeight: 1
            }}>
                {currentLocation.name}
            </Typography>

            <Box 
                sx={{ 
                    width: '100%',
                    height: 250,
                    position: 'relative',
                    '& .MuiChartsAxis-root .MuiChartsAxis-line': {
                        stroke: theme.palette.text.primary
                    },
                    '& .MuiChartsAxis-root .MuiChartsAxis-tick': {
                        stroke: theme.palette.text.primary
                    },
                    '& .MuiChartsAxis-root .MuiChartsAxis-tickLabel': {
                        fill: theme.palette.text.primary,
                        fontSize: '0.75rem'
                    }
                }}
            >
                <BarChart
                    series={[{
                        data: chartData.map(item => item.porcentaje),
                        label: 'Porcentaje de ejecución',
                        valueFormatter: (value) => `${value.toFixed(1)}%`,
                        color: theme.palette.primary.main,
                        highlightScope: {
                            highlighted: 'item',
                            faded: 'global'
                        }
                    }]}
                    xAxis={[{
                        data: completeMetrics.map(item => item.monthShort),
                        scaleType: 'band',
                        tickLabelStyle: {
                            fontSize: 11,
                            textAlign: 'center'
                        }
                    }]}
                    yAxis={[{
                        min: 0,
                        max: 100,
                        tickFormatter: (value) => `${value}%`,
                        tickNumber: 5
                    }]}
                    margin={{
                        left: 0,
                        right: 0,
                        top: 5,
                        bottom: 10
                    }}
                    tooltip={{ 
                        trigger: 'item',
                        formatter: (params) => {
                            const monthData = completeMetrics[params.index];
                            return [
                                `Mes: ${monthData.month}`,
                                `Ejecutadas: ${monthData.total_tasks_executed}`,
                                `Programadas: ${monthData.total_tasks_scheduled}`,
                                `Porcentaje: ${params.value.toFixed(1)}%`
                            ].join('\n');
                        }
                    }}
                    slotProps={{
                        legend: {
                            hidden: true
                        }
                    }}
                    sx={{
                        width: '100% !important',
                        height: '100% !important'
                    }}
                />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'right', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Seleccione el mes de corte: </Typography>
                <Select
                    value={selectMonth}
                    onChange={(e) => setSelectMonth(e.target.value)}
                    size="small"
                >
                    {MONTHS.map(month => (
                        <MenuItem key={month.value} value={month.value}>{month.full}</MenuItem>
                    ))}
                </Select>
            </Box>

            <TableContainer component={Paper}>
                <Table size="small" sx={{ minWidth: '100%' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell 
                                sx={{ 
                                    backgroundColor: theme.palette.mode === 'dark' 
                                        ? theme.palette.grey[900] 
                                        : theme.palette.primary.main,
                                    color: theme.palette.mode === 'dark' 
                                        ? theme.palette.primary.main 
                                        : theme.palette.primary.contrastText,
                                    fontWeight: 'bold'
                                }}
                            >
                                Mes
                            </TableCell>
                            <TableCell 
                                align="center" 
                                sx={{ 
                                    backgroundColor: theme.palette.mode === 'dark' 
                                        ? theme.palette.grey[900] 
                                        : theme.palette.primary.main,
                                    color: theme.palette.mode === 'dark' 
                                        ? theme.palette.primary.main 
                                        : theme.palette.primary.contrastText,
                                    fontWeight: 'bold'
                                }}
                            >
                                Actividades ejecutadas
                            </TableCell>
                            <TableCell 
                                align="center" 
                                sx={{ 
                                    backgroundColor: theme.palette.mode === 'dark' 
                                        ? theme.palette.grey[900] 
                                        : theme.palette.primary.main,
                                    color: theme.palette.mode === 'dark' 
                                        ? theme.palette.primary.main 
                                        : theme.palette.primary.contrastText,
                                    fontWeight: 'bold'
                                }}
                            >
                                Actividades programadas
                            </TableCell>
                            <TableCell 
                                align="center" 
                                sx={{ 
                                    backgroundColor: theme.palette.mode === 'dark' 
                                        ? theme.palette.grey[900] 
                                        : theme.palette.primary.main,
                                    color: theme.palette.mode === 'dark' 
                                        ? theme.palette.primary.main 
                                        : theme.palette.primary.contrastText,
                                    fontWeight: 'bold'
                                }}
                            >
                                % de ejecución
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {completeMetrics.map((row) => {
                            const percentage = row.total_tasks_scheduled > 0 
                                ? (row.total_tasks_executed / row.total_tasks_scheduled) * 100 
                                : 0;
                            
                            return (
                                <TableRow key={row.month} sx={{
                                    backgroundColor: theme.palette.mode === 'dark' 
                                        ? theme.palette.background.default
                                        : theme.palette.background.paper,
                                    '&:hover': {
                                        backgroundColor: theme.palette.action.hover,
                                    }
                                }}>
                                    <TableCell 
                                        component="th" 
                                        scope="row" 
                                        sx={{ 
                                            textTransform: 'capitalize',
                                            fontWeight: 'medium',
                                            color: theme.palette.text.primary
                                        }}
                                    >
                                        {row.month}
                                    </TableCell>
                                    <TableCell 
                                        align="center"
                                        sx={{
                                            color: theme.palette.text.primary
                                        }}
                                    >
                                        {row.total_tasks_executed}
                                    </TableCell>
                                    <TableCell 
                                        align="center"
                                        sx={{
                                            color: theme.palette.text.primary
                                        }}
                                    >
                                        {row.total_tasks_scheduled}
                                    </TableCell>
                                    <TableCell 
                                        align="center"
                                        sx={{
                                            fontWeight: 'bold',
                                            ...(row.total_tasks_scheduled === 0 ? {
                                                color: theme.palette.text.disabled,
                                                backgroundColor: theme.palette.mode === 'dark' 
                                                    ? theme.palette.grey[800]
                                                    : theme.palette.grey[200]
                                            } : percentage >= (currentLocationData?.meta || 85) ? {
                                                color: theme.palette.primary.main,
                                                backgroundColor: theme.palette.mode === 'dark'
                                                    ? theme.palette.primary.dark + '33' // 20% de opacidad
                                                    : theme.palette.primary.light + '33'
                                            } : {
                                                color: theme.palette.error.main,
                                                backgroundColor: theme.palette.mode === 'dark'
                                                    ? theme.palette.error.dark + '33'
                                                    : theme.palette.error.light + '33'
                                            })
                                        }}
                                    >
                                        {percentage.toFixed(1)}%
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}